// ⏱🔤 «말하는 낱말» 시각 — 줄마다 어절 시작 시각(회장님 2026-10-04 «1번으로 가» · 자막·장표·큰 제목을 말에 딱딱 맞추기).
//
// ★왜 있는가★ — 음성 입힐 때 받아쓰기(Soniox)가 낱말마다 시작·끝을 돌려주는데, 종전엔 줄 시작 하나만 남기고
//   나머지를 버렸다. 그래서 장표 항목·큰 제목·한 줄 안 화면 줄은 «말과 무관한» 추정(글자 비례·고른 간격)으로 떴다
//   (컬러컷 dev 2편 실측: 자막 ±0.2~0.7초 · 항목 3개가 문장 하나에 몰리면 씬 앞 80% 에 고르게).
//
// ★저장 모양(세그 병렬 배열 9번째 · `StorySegment.lineWordStarts`)★
//   `lines[i]` 와 같은 인덱스에 `{ k, s }` 또는 null.
//     k = 그 줄 글의 지문(`lineWordKey`) — 글이 바뀌면 달라진다.
//     s = 그 줄 어절(공백 단위)마다 «줄 시작(`lineTimes[i]`) 기준 상대 초» · 확정 못 한 어절은 null.
//   ★상대 초인 이유★: 씬 밀기·트림 리플처럼 `lineTimes` 만 미는 자리(11곳)를 손대지 않아도 그대로 맞는다.
//
// ★불변식(기계 검사)★ — 읽는 쪽은 반드시 `lineWordStartsFor` 하나를 거친다. 지문이 지금 글과 다르거나 어절 수가
//   다르면 null(= 종전 계산). 그래서 글을 바꾸는 자리 하나를 놓쳐도 ★틀린 시각은 절대 쓰지 않는다★(데이터를 잃을 뿐).
//
// ★이 파일은 렌더러가 import 한다★ — 무거운 모듈(supabase·story-voice)을 끌어오지 않는 순수 함수만 둔다.

/**
 * 받아쓰기 대조용 글자 정규화 — 한글(완성형+자모)·영숫자만 남긴다(문장부호·공백·[씬N] 마커 제거 · NFC).
 * ★정렬 SSOT★: `story-voice` 의 줄 시각 정렬(글자 소비·글자 단위 DP)이 이 함수를 그대로 쓴다 —
 *   어절 경계와 정렬 글자가 같은 축이어야 «어절 k 의 첫 글자»가 정렬 결과의 같은 칸을 가리킨다.
 */
export const normalizeSpeechChars = (s: string): string =>
  (s || "")
    .normalize("NFC")
    .replace(/\[[^\]]*\]/g, "")
    .replace(/[^0-9A-Za-z가-힯ㄱ-ㆎ]/g, "");

/** 한 줄의 어절(공백·줄바꿈·NBSP 단위) — 빈 조각은 버린다. */
export function lineEojeols(line: string): string[] {
  return String(line ?? "").split(/[\s ]+/u).filter((w) => w.length > 0);
}

/** 줄 글 지문(FNV-1a 32bit · 8자리 16진) — NFC·앞뒤 공백·연속 공백만 접는다(글자 하나만 바뀌어도 달라진다). */
export function lineWordKey(line: string): string {
  const s = String(line ?? "").normalize("NFC").trim().replace(/[\s ]+/gu, " ");
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, "0");
}

export interface LineWordStarts {
  /** 줄 글 지문(`lineWordKey`). */
  k: string;
  /** 어절마다 줄 시작 기준 상대 초(확정 못 한 어절 null). 길이 = 어절 수. */
  s: Array<number | null>;
}

/** 세그 하나에서 이 필드만 보는 최소 형태(렌더러 타입에 묶이지 않게). */
export interface WordStartsSegLike {
  lines?: string[];
  lineWordStarts?: Array<LineWordStarts | null | undefined> | null;
}

/**
 * ✅ 그 줄의 어절 상대 시각 — ★읽는 곳은 전부 이 함수를 거친다★(불변식의 문지기).
 * 지문 불일치·어절 수 불일치·모양 이상이면 null(= 종전 계산).
 */
export function lineWordStartsFor(seg: WordStartsSegLike | null | undefined, lineIndex: number): Array<number | null> | null {
  const line = seg?.lines?.[lineIndex];
  const entry = Array.isArray(seg?.lineWordStarts) ? seg!.lineWordStarts![lineIndex] : null;
  if (typeof line !== "string" || !entry || typeof entry !== "object") return null;
  if (typeof entry.k !== "string" || !Array.isArray(entry.s)) return null;
  if (entry.k !== lineWordKey(line)) return null;
  const n = lineEojeols(line).length;
  if (n === 0 || entry.s.length !== n) return null;
  const out: Array<number | null> = entry.s.map((v) => (typeof v === "number" && Number.isFinite(v) ? v : null));
  return out.some((v) => v !== null) ? out : null;
}

/** 🧪 지문이 지금 글과 어긋난 항목 수(기계 검사용 — 편집 뒤 «옛 시각이 남은 줄»을 센다). 읽기는 어차피 막는다. */
export function staleLineWordStartsCount(segments: ReadonlyArray<WordStartsSegLike | null | undefined>): number {
  let n = 0;
  for (const seg of segments ?? []) {
    const arr = seg?.lineWordStarts;
    if (!Array.isArray(arr)) continue;
    arr.forEach((entry, i) => {
      if (!entry) return;
      if (lineWordStartsFor(seg, i) === null) n++;
    });
  }
  return n;
}

/**
 * 어절별 정규화 글자 수 — 줄 정규화 글자열에서 어절 k 가 차지하는 칸을 잡는다.
 * 어절 정규화 합이 줄 정규화와 다르면(대괄호 마커가 어절을 가로지르는 등) null — 그 줄은 어절 시각을 못 만든다.
 */
export function eojeolCharSpans(line: string): Array<{ from: number; to: number }> | null {
  const words = lineEojeols(line);
  const spans: Array<{ from: number; to: number }> = [];
  let at = 0;
  for (const w of words) {
    const len = normalizeSpeechChars(w).length;
    spans.push({ from: at, to: at + len });
    at += len;
  }
  return at === normalizeSpeechChars(line).length ? spans : null;
}

/** 🎯 낱말 대조 재료 — 한 씬(또는 한 줄)의 «말하는 글자»마다 절대 시각. */
export interface SpokenCharStream {
  /** 정규화 글자. */
  chars: string[];
  /** 글자마다 그 글자가 속한 어절의 시작 시각(초 · 확정 못 한 어절 null). */
  at: Array<number | null>;
}

/**
 * 줄들(시간 순) → 말하는 글자 흐름. 줄마다 `lineAtSec + 어절 상대 시각`을 그 어절 글자들에 붙인다.
 * 어절 시각이 없는 줄도 글자는 싣는다(시각 null) — 대조 위치가 밀리지 않게.
 */
export function spokenCharStream(rows: ReadonlyArray<{ text: string; lineAtSec: number; wordStarts: Array<number | null> | null }>): SpokenCharStream {
  const chars: string[] = [];
  const at: Array<number | null> = [];
  for (const r of rows) {
    const spans = eojeolCharSpans(r.text);
    const norm = Array.from(normalizeSpeechChars(r.text));
    if (!spans || !r.wordStarts || r.wordStarts.length !== spans.length) {
      for (const c of norm) { chars.push(c); at.push(null); }
      continue;
    }
    spans.forEach((sp, k) => {
      const rel = r.wordStarts![k];
      const abs = typeof rel === "number" && Number.isFinite(rel) ? r.lineAtSec + rel : null;
      for (let q = sp.from; q < sp.to; q++) { chars.push(norm[q]); at.push(abs); }
    });
  }
  return { chars, at };
}

/** 최장 공통 부분열 길이와, 그 일치가 끝나는 b 쪽 마지막 칸(일치가 없으면 -1). 짧은 문구 전용(O(|a|·|b|)). */
function lcsLength(a: readonly string[], b: readonly string[]): { length: number; lastIndex: number } {
  const W = b.length + 1;
  const D = new Uint16Array((a.length + 1) * W);
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      D[i * W + j] = a[i - 1] === b[j - 1] ? D[(i - 1) * W + (j - 1)] + 1 : Math.max(D[(i - 1) * W + j], D[i * W + (j - 1)]);
    }
  }
  const length = D[a.length * W + b.length];
  let lastIndex = -1;
  for (let j = 1; j <= b.length; j++) if (D[a.length * W + j] === length) { lastIndex = j - 1; break; }
  return { length, lastIndex: length > 0 ? lastIndex : -1 };
}

/**
 * 🔎 문구(장표 항목·제목)가 말해지는 ★첫 시각★ — 흐름의 `fromChar` 이후에서 가장 이른 일치.
 *
 * 규칙(둘 중 하나를 만족하는 가장 이른 S 자리):
 *   ① «이어 붙은 일치»가 필요 길이 이상 — 필요 길이 = P 가 3자 이하면 P 전부 · 그보다 길면 max(3, P 의 절반).
 *      항목 글이 대사보다 조금 길거나(«보장 범위 확인» vs 말 «보장 범위») 머리말이 붙어도(«1. 보장 범위») 잡힌다.
 *   ② 머리 두 글자(또는 첫 글자를 뺀 두 글자)가 맞고, 그 뒤 짧은 창(|P|+3)과의 최장 공통 부분열이 P 의 70% 이상 —
 *      장표가 조사를 줄이거나(«면책·감액기간» vs 말 «면책과 감액기간») 말을 바꾼 경우(«모르면 설계사에게 묻기» vs
 *      말 «모르는 부분은 설계사에게…»)에 ①이 뒤 낱말로 늦게 잡히는 것을 막는다.
 * 반환 at = 그 일치가 시작하는 글자의 어절 시각(null 이면 그 일치는 시각을 모른다 → 다음 일치를 본다).
 * 못 찾으면 null(= 종전 계산).
 */
export function findPhraseCue(stream: SpokenCharStream, phrase: string, fromChar = 0): { atSec: number; charIndex: number; endChar: number } | null {
  const P = Array.from(normalizeSpeechChars(phrase));
  const S = stream.chars;
  if (!P.length || !S.length) return null;
  const need = P.length <= 3 ? P.length : Math.max(3, Math.ceil(P.length / 2));
  const subNeed = Math.ceil(P.length * 0.7);
  for (let j = Math.max(0, fromChar); j < S.length; j++) {
    // ② 머리 고정 차례 일치 — 문구 머리 두 글자(또는 첫 글자를 뺀 두 글자 · «꼭 확인할…» vs 말 «확인할…»)가 S[j] 에서 맞을 때,
    //   그 뒤 짧은 창(|P|+3) 과 문구의 최장 공통 부분열(LCS)이 70% 이상이면 일치.
    if (P.length >= 3) {
      for (const h of [0, 1]) {
        if (h + 1 >= P.length || S[j] !== P[h] || S[j + 1] !== P[h + 1]) continue;
        const win = S.slice(j, Math.min(S.length, j + P.length + 3));
        const lcs = lcsLength(P, win);
        const at = stream.at[j];
        if (lcs.length >= subNeed && typeof at === "number") return { atSec: at, charIndex: j, endChar: j + lcs.lastIndex + 1 };
      }
    }
    // ① S[j..] 에서 시작해 P 의 어느 자리와든 need 글자 이상 이어 붙는가(가장 긴 것).
    let best = 0;
    for (let p0 = 0; p0 + need <= P.length; p0++) {
      let L = 0;
      while (p0 + L < P.length && j + L < S.length && P[p0 + L] === S[j + L]) L++;
      if (L > best) best = L;
      if (best === P.length - p0) break;
    }
    if (best >= need) {
      const at = stream.at[j];
      if (typeof at === "number") return { atSec: at, charIndex: j, endChar: j + best };
    }
  }
  return null;
}

/**
 * 🗂 장표 종류별 «항목 문구» — 항목이 하나씩 나오는 장표만. 모르는 종류·모양 이상이면 null(시각 계산 안 함).
 * ★렌더러의 항목 순서와 같아야 한다★(layouts 의 `itemEnterFrames(p.items.length …)` 등과 같은 배열).
 */
export function slideItemPhrases(type: string, content: Record<string, unknown> | null | undefined): string[] | null {
  const c = content ?? {};
  const strs = (v: unknown) => (Array.isArray(v) && v.every((x) => typeof x === "string") ? (v as string[]) : null);
  switch (type) {
    case "checkList": return strs(c.items);
    case "stepFlow": return strs(c.steps);
    // 🏗 [건설] 예방대책 점 목록 · TBM 확인 질문 — 말하는 순간에 켜진다(사고 개요 줄은 정답지 고정 박자라 신호를 안 쓴다).
    case "preventionCard": return strs(c.preventions);
    case "tbmCard": return strs(c.checks);
    case "cardGrid": {
      const cards = c.cards;
      if (!Array.isArray(cards)) return null;
      return cards.map((x) => (x && typeof x === "object" && typeof (x as { title?: unknown }).title === "string" ? (x as { title: string }).title : ""));
    }
    case "statRow": {
      const stats = c.stats;
      if (!Array.isArray(stats)) return null;
      // 수치 행은 «라벨»을 먼저 본다 — 숫자만으로는 같은 숫자가 대사에 여러 번 나온다.
      return stats.map((x) => (x && typeof x === "object" ? String((x as { label?: unknown }).label ?? (x as { value?: unknown }).value ?? "") : ""));
    }
    default: return null;
  }
}

/** 큰 제목이 낱말을 기다리는 최대 시간(초 · 장면 시작 기준) — 넘으면 종전처럼 장면 들머리에 띄운다. */
const TITLE_CUE_MAX_WAIT_SEC = 2.5;

/** 장표 → 단어 신호(씬 상대 프레임). 렌더러가 계산해 장표 레이어로 넘긴다. 없으면 undefined(= 종전). */
export interface SlideWordCues {
  /** 항목마다 그 항목을 말하는 씬 상대 프레임(못 찾은 항목 null) — 길이 = 항목 수. */
  items?: Array<number | null>;
  /** 큰 제목을 말하는 씬 상대 프레임. */
  title?: number | null;
  /** 장표 글 지문(`slideCueKey`) — 장표 글(제목·항목)이 바뀌면(정밀 편집 등) 옛 신호를 안 쓴다. */
  k?: string;
}

/** 장표 글 지문 — 종류·제목·항목 문구로 만든다. 렌더러가 지금 장표 글로 다시 만들어 신호의 `k` 와 다르면 신호를 버린다. */
export function slideCueKey(type: string, content: Record<string, unknown> | null | undefined): string {
  const title = typeof content?.title === "string" ? content.title : "";
  return lineWordKey([type, title, ...(slideItemPhrases(type, content) ?? [])].join("\u241f"));
}

/** 신호가 지금 장표 글과 맞을 때만 돌려준다(지문 불일치·없음 = undefined = 종전). */
export function validSlideWordCues(cues: SlideWordCues | null | undefined, type: string, content: Record<string, unknown> | null | undefined): SlideWordCues | undefined {
  return cues && cues.k === slideCueKey(type, content) ? cues : undefined;
}

/**
 * 장표 하나의 단어 신호 — 씬 흐름(`stream` · 절대 초)·씬 시작(`anchorSec`)·fps 로 항목·제목 프레임을 만든다.
 * 항목은 ★앞 항목 일치 뒤에서★ 찾는다(순서 보장 · 요약 문장 «세 가지, A, B, C» 에서 차례로 잡힌다).
 */
export function slideWordCuesFrom(input: {
  type: string;
  content: Record<string, unknown> | null | undefined;
  stream: SpokenCharStream;
  anchorSec: number;
  fps: number;
}): SlideWordCues | undefined {
  const { stream, anchorSec, fps } = input;
  if (!stream.chars.length || !stream.at.some((v) => v !== null) || !(fps > 0)) return undefined;
  const toFrame = (sec: number) => Math.max(0, Math.round((sec - anchorSec) * fps));
  const out: SlideWordCues = { k: slideCueKey(input.type, input.content) };
  // 모션 타이포는 문장 시간표(beats)가 이미 말에 붙어 있다 — 제목 신호를 따로 만들지 않는다.
  const title = input.type !== "kineticTypo" && typeof input.content?.title === "string" ? (input.content.title as string) : "";
  if (title.trim()) {
    const hit = findPhraseCue(stream, title, 0);
    out.title = hit ? toFrame(hit.atSec) : null;
  }
  const phrases = slideItemPhrases(input.type, input.content);
  if (phrases && phrases.length) {
    let from = 0;
    const items: Array<number | null> = phrases.map((ph) => {
      const hit = ph.trim() ? findPhraseCue(stream, ph, from) : null;
      if (!hit) return null;
      from = hit.endChar;
      return toFrame(hit.atSec);
    });
    if (items.some((v) => v !== null)) out.items = items;
  }
  // 🧭 큰 제목은 장면의 닻이다 — 낱말이 너무 늦게(2.5초 넘게) 나오거나 항목보다 늦게 나오면 제목을 기다리지 않는다
  //   (dev 실측: «세무서의 확인 절차» 가 장면 4.5초째에 말해져 색면만 4초 · 항목이 제목보다 먼저 떴다). = 종전 시각.
  if (typeof out.title === "number") {
    const firstItem = (out.items ?? []).find((v): v is number => typeof v === "number");
    if (out.title > Math.round(TITLE_CUE_MAX_WAIT_SEC * fps) || (typeof firstItem === "number" && out.title > firstItem)) out.title = null;
  }
  return out.items || typeof out.title === "number" ? out : undefined;
}

/** 제목이 낱말을 기다릴 수 있는 마지막 순간 — 씬 내용이 빠지기 이만큼(초) 전까지는 제목이 서 있어야 읽힌다. */
const TITLE_CUE_TAIL_SEC = 0.6;

/**
 * ⏱🔤 큰 제목이 «한 자씩» 시작할 시각(초 · 씬 상대) — 낱말 신호가 있으면 그 순간, 없으면 무대 기본값(`leadSec`).
 * 무대 들어오기(쓸림·변신)가 끝나기 전엔 못 나오므로 `leadSec` 보다 이르지 않고, 씬 끝 0.6초 전보다 늦지 않다.
 */
export function titleCueLeadSec(leadSec: number, cueFrame: number | null | undefined, fps: number, contentEndSec?: number): number {
  if (typeof cueFrame !== "number" || !Number.isFinite(cueFrame) || !(fps > 0)) return leadSec;
  const cap = typeof contentEndSec === "number" && Number.isFinite(contentEndSec) ? contentEndSec - TITLE_CUE_TAIL_SEC : Number.POSITIVE_INFINITY;
  return Math.max(leadSec, Math.min(cueFrame / fps, cap));
}

/** 씬 줄 재료의 최소 형태(렌더러 세그 타입에 묶이지 않게). */
export interface SpokenRowSegLike extends WordStartsSegLike {
  lineTimes?: Array<number | null> | null;
  tLineOffsets?: Array<number | null> | null;
}

/**
 * 씬 [startIdx, endIdx) 의 «말하는 줄»을 시간 순으로 — 낱말 대조 흐름(`spokenCharStream`)의 재료.
 * 누적 체인(세그마다 앞 줄이 반복 실림)은 (글, 시각)이 같은 줄을 한 번만 싣는다. 시각 = lineTimes + 그 줄 오프셋(자막과 같은 축).
 * 시각 모르는 줄(lineTimes 없음)은 싣지 않는다 — 대조 위치를 정할 기준이 없다.
 */
export function sceneSpokenRows(
  segs: ReadonlyArray<SpokenRowSegLike | null | undefined>,
  startIdx: number,
  endIdx: number,
): Array<{ text: string; lineAtSec: number; wordStarts: Array<number | null> | null }> {
  const out: Array<{ text: string; lineAtSec: number; wordStarts: Array<number | null> | null }> = [];
  const seen = new Set<string>();
  for (let i = Math.max(0, startIdx); i < Math.min(endIdx, segs.length); i++) {
    const seg = segs[i];
    const lines = seg?.lines ?? [];
    lines.forEach((raw, li) => {
      const text = String(raw ?? "").trim();
      const t = seg?.lineTimes?.[li];
      if (!text || typeof t !== "number" || !Number.isFinite(t)) return;
      const at = t + (Number(seg?.tLineOffsets?.[li]) || 0);
      const key = `${text}\u241f${Math.round(at * 100)}`;
      const ws = lineWordStartsFor(seg, li);
      if (seen.has(key)) {
        // 같은 줄이 뒤 세그에 다시 실렸는데 그쪽에만 어절 시각이 있으면 채운다.
        const row = out.find((r) => r.text === raw && Math.round(r.lineAtSec * 100) === Math.round(at * 100));
        if (row && !row.wordStarts && ws) row.wordStarts = ws;
        return;
      }
      seen.add(key);
      out.push({ text: raw, lineAtSec: at, wordStarts: ws });
    });
  }
  return out.sort((a, b) => a.lineAtSec - b.lineAtSec);
}

/**
 * 장면 하나의 장표 낱말 신호 — 렌더러(Overlay169)와 정밀 편집 인계(`collectSourceSlides`)가 ★같이 부르는★ 한 창구.
 * @param captionOffsetSec 자막 축 오프셋(렌더러 `captionOffsetSec` · 말이 그려지는 축과 씬 비주얼 축을 맞춘다).
 */
export function sceneSlideWordCues(
  segs: ReadonlyArray<SpokenRowSegLike & { t?: number } | null | undefined>,
  startIdx: number,
  endIdx: number,
  slide: { type: string; content: Record<string, unknown> | null | undefined },
  fps: number,
  captionOffsetSec = 0,
): SlideWordCues | undefined {
  const rows = sceneSpokenRows(segs, startIdx, endIdx);
  if (!rows.some((r) => r.wordStarts)) return undefined;
  return slideWordCuesFrom({
    type: slide.type,
    content: slide.content,
    stream: spokenCharStream(rows),
    anchorSec: (Number(segs[startIdx]?.t) || 0) - captionOffsetSec,
    fps,
  });
}

/** 자막 축 오프셋(초) — Overlay169 렌더러와 같은 식(`captionOffsetSec` 이 0 이상 유한수면 그 값, 아니면 `voiceOffsetSec`). */
export function storyCaptionOffsetSec(data: { voiceOffsetSec?: unknown; captionOffsetSec?: unknown } | null | undefined): number {
  const voice = Math.max(0, Number(data?.voiceOffsetSec) || 0);
  const cap = data?.captionOffsetSec;
  return typeof cap === "number" && Number.isFinite(cap) && cap >= 0 ? cap : voice;
}
